import * as React from "react";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { LibraryItem, PackageItem } from "./external-libraries.types";
import LibraryDetail from "./library-detail";
import LibraryEmptyState from "./library-empty-state";

interface PackageRowProps {
  pkg: PackageItem;
  isInstalled: boolean;
  isAdding: boolean;
  onOpen: () => void;
  onAdd: (packageId: string, version: string) => void;
}

const PackageRow: React.FC<PackageRowProps> = ({ pkg, isInstalled, isAdding, onOpen, onAdd }) => {
  const handleAdd = (e?: React.MouseEvent): void => {
    e?.stopPropagation();
    onAdd(pkg.id, pkg.version);
  };

  return (
    <div
      className="kuika-external-libraries__row kuika-external-libraries__row--package"
      data-testid={`externalLibrariesPackageRow-${pkg.id}`}
    >
      <div
        className={`kuika-external-libraries__row-head ${OVERFLOW_REVEAL_HOST_CLASS}`}
        role="button"
        tabIndex={0}
        aria-label={`View ${pkg.id}`}
        onClick={onOpen}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        <KuikaSvg name="package" width={16} height={16} className="kuika-external-libraries__row-icon" />
        <div className="kuika-external-libraries__row-text">
          <KuikaTextMedium className="kuika-external-libraries__row-name">
            <KuikaOverflowText>{pkg.id}</KuikaOverflowText>
          </KuikaTextMedium>
          {pkg.description && (
            <KuikaSmallText className="kuika-external-libraries__row-subtitle">{pkg.description}</KuikaSmallText>
          )}
        </div>
        {pkg.version && <span className="kuika-external-libraries__version-chip">{pkg.version}</span>}
        {isInstalled ? (
          <span
            className="kuika-external-libraries__installed-flag"
            data-testid="externalLibrariesPackageInstalledFlag"
          >
            <KuikaSvg name="check" width={14} height={14} />
            Installed
          </span>
        ) : (
          <KuikaButton
            type="primary"
            size="sm"
            loading={isAdding}
            onClick={handleAdd}
            data-testid="externalLibrariesPackageAddButton"
          >
            Add
          </KuikaButton>
        )}
      </div>
    </div>
  );
};

interface PackageListProps {
  packages: PackageItem[];
  loading: boolean;
  loadingMore: boolean;
  search: string;
  installed: LibraryItem[];
  addingId: string | undefined;
  hasMore: boolean;
  onAdd: (packageId: string, version: string) => void;
  onLoadMore: () => void;
}

const PackageList: React.FC<PackageListProps> = ({
  packages,
  loading,
  loadingMore,
  search,
  installed,
  addingId,
  hasMore,
  onAdd,
  onLoadMore
}) => {
  const [detailId, setDetailId] = React.useState<string | undefined>(undefined);
  const listPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: loading,
    hasData: packages.length > 0
  });

  const isInstalled = (pkg: PackageItem): boolean =>
    installed.some((library) => library.name === pkg.id && library.version === pkg.version);

  const detailPackage: PackageItem | undefined = detailId ? packages.find((pkg) => pkg.id === detailId) : undefined;
  if (detailPackage) {
    const installedAlready: boolean = isInstalled(detailPackage);
    const action: React.ReactNode = installedAlready ? (
      <span className="kuika-external-libraries__installed-flag">
        <KuikaSvg name="check" width={14} height={14} />
        Installed
      </span>
    ) : (
      <KuikaButton
        type="primary"
        size="sm"
        loading={addingId === detailPackage.id}
        onClick={() => onAdd(detailPackage.id, detailPackage.version)}
      >
        Add
      </KuikaButton>
    );

    return (
      <LibraryDetail
        name={detailPackage.id}
        version={detailPackage.version}
        description={detailPackage.description}
        action={action}
        onBack={() => setDetailId(undefined)}
      />
    );
  }

  if (packages.length === 0 && !loading) {
    if (search) {
      return (
        <LibraryEmptyState icon="search-off" title="No packages found" description={`Nothing matches “${search}”.`} />
      );
    }
    return (
      <LibraryEmptyState
        icon="search"
        title="Search the registry"
        description="Find a package by name, then open it to read the details and add it."
      />
    );
  }

  if (listPresentation === "skeleton") {
    return (
      <KuikaSkeletonRegion label="Loading packages" className="kuika-external-libraries__loader">
        <KuikaSkeletonList rows={6} lines={2} />
      </KuikaSkeletonRegion>
    );
  }

  return (
    <div className="kuika-external-libraries__loader">
      {packages.map((pkg) => (
        <PackageRow
          key={`${pkg.id}-${pkg.version}`}
          pkg={pkg}
          isInstalled={isInstalled(pkg)}
          isAdding={addingId === pkg.id}
          onOpen={() => setDetailId(pkg.id)}
          onAdd={onAdd}
        />
      ))}
      {hasMore && (
        <div className="kuika-external-libraries__load-more">
          <KuikaButton type="secondary" size="sm" loading={loadingMore} onClick={onLoadMore}>
            Load more
          </KuikaButton>
        </div>
      )}
    </div>
  );
};

export default PackageList;
